import { QueryClient, useQuery } from "react-query";
import { httpClient } from "src/shared/services/httpClient";
import { CHAMBER_COUNT_QUERY_KEY } from "src/shared/enums/useQueryKeys";

/**
 * Μετρητές (counts) του dashboard ΥΓΕΜΗ.
 *
 * Αντικαθιστούν τα counts που παλιά "ψήνονταν" στο session κατά το login (αργό login,
 * stale δεδομένα). Κάθε count είναι ξεχωριστό GET action στον ChamberController και
 * cached για 10 λεπτά. Μετά από ενέργειες του χρήστη (ανάθεση, forward, κλείσιμο κ.λπ.)
 * γίνεται targeted invalidation μόνο των counts που επηρεάζονται.
 */

export type TCountResponse = { count: number | null };

const STALE_TIME = 10 * 60 * 1000; // 10 λεπτά

/** Το όνομα κάθε count == όνομα του action στον ChamberController == segment του query key. */
function useChamberCount(name: string, enabled: boolean) {
  return useQuery({
    queryKey: [CHAMBER_COUNT_QUERY_KEY, name],
    queryFn: () => httpClient.get<TCountResponse>(`/chamber/${name}`),
    staleTime: STALE_TIME,
    enabled,
    // Σιωπηλή αποτυχία: κανένα snackbar (override του default onError του QueryClient).
    // Σε σφάλμα δεν υπάρχει data -> count null -> το badge κρύβεται.
    onError: () => {},
  });
}

export function useActiveOwnApplicationsCount(enabled = true) {
  return useChamberCount("activeOwnApplicationsCount", enabled);
}

export function useActiveOwnOnlyApplicationsCount(enabled = true) {
  return useChamberCount("activeOwnOnlyApplicationsCount", enabled);
}

export function useOwnRepresentativesApplicationCount(enabled = true) {
  return useChamberCount("ownRepresentativesApplicationCount", enabled);
}

export function useRepresentativesApplicationCount(enabled = true) {
  return useChamberCount("representativesApplicationCount", enabled);
}

export function useActiveChamberApplicationsPaidCount(enabled = true) {
  return useChamberCount("activeChamberApplicationsPaidCount", enabled);
}

export function useActiveChamberApplicationsUnpaidCount(enabled = true) {
  return useChamberCount("activeChamberApplicationsUnpaidCount", enabled);
}

export function useActiveOwnCertificateApplicationsCount(enabled = true) {
  return useChamberCount("activeOwnCertificateApplicationsCount", enabled);
}

export function useActiveChamberCertificateApplicationsPaidCount(enabled = true) {
  return useChamberCount("activeChamberCertificateApplicationsPaidCount", enabled);
}

export function useActiveChamberCertificateApplicationsUnpaidCount(
  enabled = true,
) {
  return useChamberCount("activeChamberCertificateApplicationsUnpaidCount", enabled);
}

export function useUnsignedOwnCertificateApplicationsCount(enabled = true) {
  return useChamberCount("unsignedOwnCertificateApplicationsCount", enabled);
}

export function useActiveOwnViolationCasesCount(enabled = true) {
  return useChamberCount("activeOwnViolationCasesCount", enabled);
}

export function useUnassignedViolationCasesCount(enabled = true) {
  return useChamberCount("unassignedViolationCasesCount", enabled);
}

export function useActiveOwnComplaintsCount(enabled = true) {
  return useChamberCount("activeOwnComplaintsCount", enabled);
}

export function useUnassignedComplaintsCount(enabled = true) {
  return useChamberCount("unassignedComplaintsCount", enabled);
}

export function useActiveChamberSampleInspectionsCount(enabled = true) {
  return useChamberCount("activeChamberSampleInspectionsCount", enabled);
}

export function useActiveOwnSampleCasesCount(enabled = true) {
  return useChamberCount("activeOwnSampleCasesCount", enabled);
}

/**
 * Οικογένειες counts ανά domain — για targeted invalidation μετά από ενέργειες του χρήστη.
 * Τα strings πρέπει να ταιριάζουν με τα ονόματα των actions/hooks παραπάνω.
 */
export const CHAMBER_APPLICATION_COUNTS = [
  "activeOwnApplicationsCount",
  "activeOwnOnlyApplicationsCount",
  "activeChamberApplicationsPaidCount",
  "activeChamberApplicationsUnpaidCount",
];

export const CHAMBER_CERTIFICATE_COUNTS = [
  "activeOwnCertificateApplicationsCount",
  "activeChamberCertificateApplicationsPaidCount",
  "activeChamberCertificateApplicationsUnpaidCount",
  "unsignedOwnCertificateApplicationsCount",
];

export const CHAMBER_REPRESENTATIVES_COUNTS = [
  "ownRepresentativesApplicationCount",
  "representativesApplicationCount",
];

export const CHAMBER_VIOLATION_COUNTS = [
  "activeOwnViolationCasesCount",
  "unassignedViolationCasesCount",
];

export const CHAMBER_COMPLAINT_COUNTS = [
  "activeOwnComplaintsCount",
  "unassignedComplaintsCount",
];

export const CHAMBER_SAMPLE_COUNTS = [
  "activeChamberSampleInspectionsCount",
  "activeOwnSampleCasesCount",
];

/**
 * Invalidate συγκεκριμένα counts (ώστε να γίνει refetch με φρέσκα δεδομένα).
 */
export function invalidateChamberCounts(
  queryClient: QueryClient,
  names: string[],
) {
  return Promise.all(
    names.map((name) =>
      queryClient.invalidateQueries([CHAMBER_COUNT_QUERY_KEY, name]),
    ),
  );
}
